import React from 'react';
import PropTypes from 'prop-types';
import { localize } from 'react-localize-redux';
import moment from 'moment';
import RaisedButton from 'material-ui/RaisedButton';
import styled from 'styled-components';
import FloatingActionButton from 'material-ui/FloatingActionButton';
import ContentCreate from 'material-ui/svg-icons/content/create';
import { Link } from 'react-router';
import { grey200, grey500 } from 'material-ui/styles/colors';
import { translate } from 'react-i18next';

import CommonTable from './CommonTable';
import NoteFormDialog from './NoteFormDialog';
import { dateFromISO } from '../utils/helpers';
import TextFilterField from '../components/filters/TextFilterField';
import DateRangeFilterField from '../components/filters/DateRangeFilterField';
import i18n from '../translations/i18n';

const propTypes = {
  currentUserId: PropTypes.any,
  onNoteSave: PropTypes.func,
  notes: PropTypes.any,
  t: PropTypes.func,
  loading: PropTypes.any,
  onChange: PropTypes.func,
};

const defaultProps = {};

const ItemActionButton = styled(FloatingActionButton)`
  fill: ${grey500} !important;
  height: 30px !important;
  width: 30px !important;
`;

const ContentCreateStyled = styled(ContentCreate)`
  height: 20px !important;
  width: 20px !important;
`;

class NotesTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      noteId: null,
      comment: null,
      noteFormDialogShow: false,
    };
    this.defaultSorting = [
      {
        id: 'id',
        desc: true,
      },
    ];
  }
  /*
    Custom methods
   */
  initColumns() {
    this.columns = [
      {
        header: this.props.t('id'),
        accessor: 'id',
        className: 'centerAlign',
        width: 60,
        filterMethod: (filter, row) => parseInt(filter.value, 10) === parseInt(row.id, 10),
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField name="id" filter={filter} onFilterChange={onFilterChange} />
        ),
      },
      {
        header: this.props.t('username'),
        accessor: 'account___username',
        width: 100,
        filterMethod: (filter, row) =>
          row.account___username.toLowerCase().startsWith(filter.value.toLowerCase()),
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField
            name="account___username"
            filter={filter}
            onFilterChange={onFilterChange}
          />
        ),
      },
      {
        header: this.props.t('comment'),
        accessor: 'comment',
        width: 400,
        filterMethod: (filter, row) =>
          row.comment.toLowerCase().startsWith(filter.value.toLowerCase()),
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField name="comment" filter={filter} onFilterChange={onFilterChange} />
        ),
      },
      {
        header: this.props.t('time_created'),
        accessor: 'date_created',
        width: 200,
        render: row => dateFromISO(row.value),
        filterRender: ({ filter, onFilterChange }) => (
          <DateRangeFilterField
            name="date_created"
            filter={filter}
            onFilterChange={onFilterChange}
          />
        ),
        filterMethod: (filter, row) => {
          if (!filter.value) return true;
          const startDate = filter.value.startDate.startOf('day');
          const endDate = filter.value.endDate.endOf('day');
          const rowDate = moment(row.date_created);

          return rowDate.isAfter(startDate) && rowDate.isBefore(endDate);
        },
      },
      {
        header: this.props.t('time_modified'),
        accessor: 'date_modified',
        width: 200,
        render: row => dateFromISO(row.value),
        filterRender: ({ filter, onFilterChange }) => (
          <DateRangeFilterField
            name="date_modified"
            filter={filter}
            onFilterChange={onFilterChange}
          />
        ),
        filterMethod: (filter, row) => {
          if (!filter.value) return true;
          const startDate = filter.value.startDate.startOf('day');
          const endDate = filter.value.endDate.endOf('day');
          const rowDate = moment(row.date_modified);

          return rowDate.isAfter(startDate) && rowDate.isBefore(endDate);
        },
      },
      {
        header: '',
        width: 60,
        sortable: false,
        hideFilter: true,
        className: 'centerAlign',
        render: (data) => {
          const now = moment(new Date());
          const dateCreated = moment(data.row.date_created);

          const duration = moment.duration(now.diff(dateCreated)).asMinutes();
          if (this.props.currentUserId !== data.row.account_id || Math.ceil(duration) > 60) {
            return '';
          }
          return (
            <Link
              className="button"
              onClick={() => this.showNoteFormModal(data.row.id, data.row.comment)}
            >
              <ItemActionButton
                zDepth={0}
                mini
                backgroundColor={grey200}
                iconStyle={{
                  fill: grey500,
                  height: 30,
                  width: 30,
                }}
              >
                <ContentCreateStyled />
              </ItemActionButton>
            </Link>
          );
        },
      },
    ];
  }

  showNoteFormModal = (noteId = null, comment = null) => {
    this.setState({
      comment,
      noteId,
      noteFormDialogShow: true,
    });
  };

  hideNoteDialog = () => {
    this.setState({
      noteFormDialogShow: false,
    });
  };

  saveNote = (note) => {
    if (this.state.noteId) {
      note.id = this.state.noteId;
    }
    this.props.onNoteSave(note);
    this.hideNoteDialog();
  };

  render() {
    this.initColumns();

    let data = [];

    if (this.props.notes) {
      data = Object.values(this.props.notes);
    }

    const newNoteButton = (
      <RaisedButton
        label={this.props.t('new_note')}
        onClick={() => {
          this.showNoteFormModal();
        }}
      />
    );

    return (
      <div>
        <CommonTable
          title={this.props.t('notes')}
          data={data}
          rightComponent={newNoteButton}
          defaultPageSize={10}
          defaultSorting={this.defaultSorting}
          onItemEdit={this.itemEdit}
          loading={this.props.loading}
          columns={this.columns}
          onChange={this.props.onChange}
          className="notes-table"
        />
        {this.state.noteFormDialogShow && (
          <NoteFormDialog
            open={this.state.noteFormDialogShow}
            comment={this.state.comment}
            onConfirm={this.saveNote}
            onCancel={this.hideNoteDialog}
          />
        )}
      </div>
    );
  }
}

NotesTable.propTypes = propTypes;
NotesTable.defaultProps = defaultProps;

const extended = translate(['translations'], { i18n })(NotesTable);
export default localize(extended, 'locale');



// WEBPACK FOOTER //
// ./src/components/NotesTable.js